import React, { forwardRef, useRef } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import mergeRefs from "merge-refs";
import { Link } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { Icon, IconVariant } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useHover } from "src/hooks/usehooks-ts";

type ToolbarProps = {
  dataLoc?: string;
  children: React.ReactNode;
} & Omit<
  React.ComponentPropsWithoutRef<"div">,
  "className" | "style" | "role" | "children"
>;

type ToolbarIconButtonProps = {
  icon: IconProp;
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
  dataLoc?: string;
};

type ToolbarIconLinkProps = {
  icon: IconProp;
  tooltip: string;
  disabled?: boolean;
  dataLoc?: string;
  openInNewTab?: boolean;
} & ({ href: string } | { to: string });

const getIconVariant = (disabled: boolean, isHovered: boolean): IconVariant =>
  disabled ? "disabled" : isHovered ? "inverted" : "default";

const useToolbarItem = <T extends HTMLElement>(disabled: boolean) => {
  const innerRef = useRef<T>(null);
  const isHovered = useHover(innerRef);
  const iconVariant = getIconVariant(disabled, isHovered);
  return { innerRef, iconVariant };
};

const ToolbarIconButton = forwardRef<HTMLButtonElement, ToolbarIconButtonProps>(
  ({ icon, tooltip, onClick, disabled = false, dataLoc }, ref) => {
    const { innerRef, iconVariant } =
      useToolbarItem<HTMLButtonElement>(disabled);

    return (
      <Tooltip placement="top" title={tooltip} delay={200} asChild>
        <button
          ref={mergeRefs(ref, innerRef)}
          type="button"
          aria-label={tooltip}
          className={twJoin(
            "flex items-center justify-center p-2 transition-all",
            !disabled && "hover:bg-indigo-500",
          )}
          data-loc={dataLoc}
          disabled={disabled}
          onClick={onClick}
        >
          <Icon variant={iconVariant} icon={icon} size="xs" />
        </button>
      </Tooltip>
    );
  },
);

const ToolbarIconLink = forwardRef<HTMLAnchorElement, ToolbarIconLinkProps>(
  (props, ref) => {
    const { icon, tooltip, disabled = false, dataLoc, openInNewTab } = props;
    const { innerRef, iconVariant } =
      useToolbarItem<HTMLAnchorElement>(disabled);

    const className = twJoin(
      "flex items-center justify-center p-2 transition-all",
      disabled ? "cursor-default" : "hover:bg-indigo-500",
    );

    const sharedProps = {
      ref: mergeRefs(ref, innerRef),
      className,
      "aria-label": tooltip,
      "data-loc": dataLoc,
      "aria-disabled": disabled || undefined,
      tabIndex: disabled ? -1 : undefined,
      rel: "noreferrer noopener",
      target: openInNewTab ? ("_blank" as const) : undefined,
      onClick: disabled
        ? (e: React.MouseEvent) => e.preventDefault()
        : undefined,
    };

    const iconElement = <Icon variant={iconVariant} icon={icon} size="xs" />;

    return (
      <Tooltip placement="top" title={tooltip} delay={200} asChild>
        {"href" in props ? (
          <a {...sharedProps} href={props.href}>
            {iconElement}
          </a>
        ) : (
          <Link {...sharedProps} to={props.to}>
            {iconElement}
          </Link>
        )}
      </Tooltip>
    );
  },
);

/** Figma: https://www.figma.com/design/BO9DWO8PKNGNSI3HhpSobA/Design-System-v2?node-id=5234-43685&m=dev */
export const Toolbar: React.FC<ToolbarProps> & {
  IconButton: typeof ToolbarIconButton;
  IconLink: typeof ToolbarIconLink;
} = ({ dataLoc, children, ...restOfProps }) => (
  <div
    {...restOfProps}
    className="flex flex-row divide-x divide-gray-100 overflow-hidden rounded-lg bg-white shadow-lg"
    data-loc={dataLoc}
    role="toolbar"
  >
    {children}
  </div>
);

Toolbar.IconButton = ToolbarIconButton;
Toolbar.IconLink = ToolbarIconLink;
